import { Box, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { PREVIEW_ICON_GAP } from "components/PreviewFeature/constants";
import { PreviewFeatureIndicator } from "components/PreviewFeature/PreviewFeatureIndicator";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCallback } from "react";
import { Navigate, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

function getCurrentView(pathname: string): string {
  if (pathname.includes("/steers")) return "steers";
  if (pathname.includes("/rcra-info")) return "rcra-info";
  if (pathname.includes("/manage")) return "manage";
  return "epcra";
}

export function CredentialsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  const epcraMatch = useMatch("/staff/credentials/epcra/:credentialImplementationId?");
  const rcraMatch = useMatch("/staff/credentials/rcra-info");
  const steersMatch = useMatch("/staff/credentials/steers");
  const manageMatch = useMatch("/staff/credentials/manage");
  const isOverview = epcraMatch ?? rcraMatch ?? steersMatch ?? manageMatch;

  useBreadcrumb({
    label: "Credentials",
    to: "/staff/credentials",
  });

  const currentView = getCurrentView(location.pathname);

  const handleViewChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
      if (!newView) return;
      navigate(`/staff/credentials/${newView}`);
    },
    [navigate]
  );

  if (!isOverview && location.pathname === "/staff/credentials") {
    return <Navigate to="/staff/credentials/epcra" replace />;
  }

  return (
    <Box>
      {isOverview && (
        <Stack
          sx={{
            backgroundColor: "background.paper",
            borderBottom: "1px solid",
            borderBottomColor: "divider",
          }}
        >
          <ToggleButtonGroup
            value={currentView}
            exclusive
            onChange={handleViewChange}
            sx={{
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: 0,
                color: "text.secondary",
                pb: 1.5,
                px: 3,
                mr: 3,
                "&.Mui-selected": {
                  color: "primary.main",
                  backgroundColor: "transparent",
                  borderBottom: "3px solid",
                  borderBottomColor: "primary.main",
                },
              },
            }}
          >
            <ToggleButton value="epcra">EPCRA Credential Management</ToggleButton>
            <ToggleButton value="rcra-info">RCRAInfo Credentials</ToggleButton>
            <ToggleButton value="steers">STEERS Credentials</ToggleButton>
            <ToggleButton value="manage">
              {/* Unconditional rather than `StaffInDevelopmentIcon`: that one keys off a
                  feature flag being unset for the current tenant, and a staff-only route
                  has neither. Same Handyman icon so it reads as the marker used on
                  Manifests and Waste Streams. */}
              <Stack direction="row" alignItems="center" spacing={PREVIEW_ICON_GAP}>
                <span>Credential Management</span>
                <PreviewFeatureIndicator
                  inline
                  color="inherit"
                  title="This page is in development and not yet ready for general use."
                />
              </Stack>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      <Outlet />
    </Box>
  );
}
